/**
 * LegalDocument - shared renderer for the lawyer-drafted legal pages
 * (הצהרת נגישות / מדיניות פרטיות / תקנון אתר ותנאי שימוש).
 *
 * The source documents are numbered Word documents with up to three list levels
 * (1. / 1.1 / 1.1.1). Rather than hand-writing the numbers into the JSX — where
 * they drift the moment a clause is inserted — the page declares the document as
 * data and this component derives every number from position.
 *
 * Accessibility: sections are real <h2>s, clauses are real <ol>/<li>s (numbering
 * suppressed visually because the legal number is rendered inline as text), so
 * screen readers still announce list structure and position.
 */

import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

/**
 * The service address as named in the legal documents. Shared, because the
 * privacy policy and the terms must state the same one — they were briefly
 * inconsistent with the live app when each page carried its own copy.
 *
 * Note the spelling: the live domain is iftach-adviCe.com. The signed .docx
 * files carry an older "iftach-advise" address; if counsel reissues them, that
 * is the field to have corrected there too.
 */
export const SITE_URL = 'https://app.iftach-advice.com'

/** A sub-clause (level 3, e.g. 4.2.1). Plain text only. */
export type SubClause = string

/** A clause (level 2, e.g. 4.2). `notes` are unnumbered paragraphs that follow it. */
export interface Clause {
  text: ReactNode
  sub?: SubClause[]
  notes?: ReactNode[]
}

/** A section (level 1, e.g. 4). `intro` are unnumbered paragraphs under the heading. */
export interface Section {
  title: string
  intro?: ReactNode[]
  clauses?: Clause[]
}

/**
 * An unnumbered paragraph following a clause. Plain text keeps <p> semantics;
 * richer nodes (e.g. the contact list) render in a <div>, since block elements
 * are not valid inside <p>.
 */
function Note({ children, className }: { children: ReactNode; className?: string }) {
  return typeof children === 'string' ? (
    <p className={className}>{children}</p>
  ) : (
    <div className={className}>{children}</div>
  )
}

interface LegalDocumentProps {
  /** Page heading, e.g. "מדיניות פרטיות" */
  title: string
  /** Sub-heading shown under the title, e.g. the document's own formal title */
  subtitle?: string
  /** "יולי 2026" — as stated at the foot of the signed document */
  lastUpdated: string
  /** Unnumbered opening paragraphs, before section 1 */
  preamble?: ReactNode[]
  sections: Section[]
  /**
   * Whether the source document numbers its sections (1. / 1.1 / 1.1.1).
   * The privacy policy and terms do; the accessibility statement uses plain
   * headings with bullets, so it passes `numbered={false}`.
   */
  numbered?: boolean
  /** Free-form content rendered after the numbered sections (contact block etc.) */
  children?: ReactNode
}

export default function LegalDocument({
  title,
  subtitle,
  lastUpdated,
  preamble = [],
  sections,
  numbered = true,
  children,
}: LegalDocumentProps) {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <article
        dir="rtl"
        className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 leading-relaxed text-gray-800"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
        {subtitle && <p className="text-base text-gray-700 mb-1">{subtitle}</p>}
        <p className="text-sm text-gray-500 mb-6">עודכן לאחרונה: {lastUpdated}</p>

        {preamble.map((p, i) => (
          <p key={`pre-${i}`} className="mb-3">
            {p}
          </p>
        ))}

        {sections.map((section, si) => {
          const n = si + 1
          return (
            <section key={`s-${si}`} className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                {numbered ? `${n}. ` : ''}
                {section.title}
              </h2>

              {section.intro?.map((p, i) => (
                <p key={`s-${si}-i-${i}`} className="mb-2">
                  {p}
                </p>
              ))}

              {!numbered && section.clauses && section.clauses.length > 0 && (
                <ul className="list-disc pr-6 space-y-1.5">
                  {section.clauses.map((clause, ci) => (
                    <li key={`s-${si}-c-${ci}`}>
                      {clause.text}
                      {clause.sub && clause.sub.length > 0 && (
                        <ul className="list-[circle] pr-6 mt-1.5 space-y-1">
                          {clause.sub.map((sub, sbi) => (
                            <li key={`s-${si}-c-${ci}-sc-${sbi}`}>{sub}</li>
                          ))}
                        </ul>
                      )}
                      {clause.notes?.map((note, ni) => (
                        <Note key={`s-${si}-c-${ci}-n-${ni}`} className="mt-1.5">
                          {note}
                        </Note>
                      ))}
                    </li>
                  ))}
                </ul>
              )}

              {numbered && section.clauses && section.clauses.length > 0 && (
                <ol className="list-none p-0 m-0 space-y-2">
                  {section.clauses.map((clause, ci) => (
                    <li key={`s-${si}-c-${ci}`}>
                      <div className="flex gap-2">
                        <span className="shrink-0 font-semibold text-gray-600 tabular-nums" dir="ltr">
                          {n}.{ci + 1}
                        </span>
                        <div className="flex-1">
                          <span>{clause.text}</span>

                          {clause.sub && clause.sub.length > 0 && (
                            <ol className="list-none p-0 mt-2 space-y-1.5">
                              {clause.sub.map((sub, sbi) => (
                                <li key={`s-${si}-c-${ci}-sc-${sbi}`} className="flex gap-2">
                                  <span
                                    className="shrink-0 font-semibold text-gray-500 tabular-nums"
                                    dir="ltr"
                                  >
                                    {n}.{ci + 1}.{sbi + 1}
                                  </span>
                                  <span className="flex-1">{sub}</span>
                                </li>
                              ))}
                            </ol>
                          )}

                          {clause.notes?.map((note, ni) => (
                            <Note key={`s-${si}-c-${ci}-n-${ni}`} className="mt-2">
                              {note}
                            </Note>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )
        })}

        {children}

        <div className="mt-10 pt-6 border-t border-gray-200">
          <Link to="/" className="text-primary-700 underline hover:text-primary-800">
            ← חזרה לדף הבית
          </Link>
        </div>
      </article>
    </div>
  )
}

/** Contact block shared by the legal pages — phone + email as actionable links. */
export function LegalContact({ phone, email }: { phone: string; email: string }) {
  return (
    <ul className="list-none p-0 space-y-1 mt-2">
      <li>
        <strong>טלפון:</strong>{' '}
        <a className="text-primary-700 underline" href={`tel:${phone.replace(/-/g, '')}`} dir="ltr">
          {phone}
        </a>
      </li>
      <li>
        <strong>דוא"ל:</strong>{' '}
        <a className="text-primary-700 underline" href={`mailto:${email}`} dir="ltr">
          {email}
        </a>
      </li>
    </ul>
  )
}
